import firebase from "../firebaseConfig";

/**
 * Service pour nettoyer les données FlowTracker
 */
const flowTrackerCleanupService = {
  /**
   * Nettoie les données FlowTracker d'un utilisateur en supprimant les champs vides
   * @param {string} userId - ID de l'utilisateur
   * @returns {Promise<Object>} - Statistiques de nettoyage
   */
  async cleanupFlowTrackerData(userId) {
    if (!userId) {
      throw new Error("User ID is required");
    }

    try {
      // Récupérer les données utilisateur
      const userRef = firebase.firestore().collection('users').doc(userId);
      const userDoc = await userRef.get();

      if (!userDoc.exists || !userDoc.data().FlowTrackerData) {
        return {
          success: false,
          message: "Aucune donnée FlowTracker trouvée",
          entriesCleaned: 0,
          spaceReduced: 0
        };
      }

      const userData = userDoc.data();
      const flowTrackerData = userData.FlowTrackerData || [];
      
      // Calculer la taille avant nettoyage
      const beforeSize = new TextEncoder().encode(JSON.stringify(flowTrackerData)).length;
      
      // Nettoyer les données
      const cleanedData = flowTrackerData.map(entry => {
        return {
          date: entry.date,
          data: this.cleanEntryData(entry.data)
        };
      });
      
      // Calculer la taille après nettoyage
      const afterSize = new TextEncoder().encode(JSON.stringify(cleanedData)).length;
      
      // Mettre à jour les données dans Firestore
      await userRef.update({
        FlowTrackerData: cleanedData
      });
      
      return {
        success: true,
        message: "Données FlowTracker nettoyées avec succès",
        entriesCleaned: flowTrackerData.length,
        spaceReduced: beforeSize - afterSize,
        percentReduced: Math.round(((beforeSize - afterSize) / beforeSize) * 100)
      };
    } catch (error) {
      console.error("Erreur lors du nettoyage des données FlowTracker:", error);
      throw error;
    }
  },

  /**
   * Nettoie les données d'une entrée FlowTracker en supprimant les champs vides
   * @param {Object} entryData - Données d'une entrée FlowTracker
   * @returns {Object} - Données nettoyées
   */
  cleanEntryData(entryData) {
    if (!entryData) return {};
    
    const cleanedData = {};
    
    // Liste des champs à conserver même s'ils sont vides
    const requiredFields = ['grandTotal', 'meanFlowQuality'];
    
    // Parcourir tous les champs de l'entrée
    Object.keys(entryData).forEach(key => {
      const value = entryData[key];
      
      // Vérifier si c'est un champ de catégorie vide
      if (key.startsWith('category_') && (value === '' || value === null || value === undefined)) {
        return; // Ne pas inclure ce champ
      }
      
      // Vérifier si c'est un champ de notation vide
      if (key.startsWith('notation_') && (value === '' || value === null || value === undefined)) {
        return; // Ne pas inclure ce champ
      }
      
      // Vérifier si c'est un champ de temps à "00:00"
      if (key.startsWith('time') && value === '00:00') {
        return; // Ne pas inclure ce champ
      }
      
      // Conserver les champs requis ou non vides
      if (requiredFields.includes(key) || value !== '' && value !== null && value !== undefined) {
        cleanedData[key] = value;
      }
    });
    
    return cleanedData;
  },
  
  /**
   * Vérifie si les composants sont compatibles avec la structure nettoyée
   * @param {Object} entryData - Données d'une entrée FlowTracker
   * @returns {Object} - Données avec valeurs par défaut pour compatibilité
   */
  ensureCompatibility(entryData) {
    const compatibleData = { ...entryData };
    
    // Assurer la présence des champs requis pour Daily Routine
    for (let i = 1; i <= 7; i++) {
      // Catégories
      if (!compatibleData[`category_${i}`]) {
        compatibleData[`category_${i}`] = '';
      }
      
      // Notations
      if (!compatibleData[`notation_${i}`]) {
        compatibleData[`notation_${i}`] = '';
      }
      
      // Temps
      if (!compatibleData[`time${i}_0`]) {
        compatibleData[`time${i}_0`] = '00:00';
      }
      if (!compatibleData[`time${i}_1`]) {
        compatibleData[`time${i}_1`] = '00:00';
      }
    }
    
    // Autres champs requis
    if (!compatibleData.grandTotal) {
      compatibleData.grandTotal = '00:00';
    }
    if (!compatibleData.meanFlowQuality) {
      compatibleData.meanFlowQuality = '0.00';
    }
    
    return compatibleData;
  }
};

export default flowTrackerCleanupService;
